(() => {
  'use strict';
  if (window.UniMotion) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const initialMotion = document.body.dataset.motion || 'subtle';
  const preferenceKey = 'unisystem:motion-paused';
  const instances = new Map();
  const animations = new Set();
  const panelStates = new WeakMap();
  const depthPhotos = new Set();
  let depthFrame = null;
  const measureDepth = () => {
    depthFrame = null;
    depthPhotos.forEach(photo => {
      if (!allowed(photo) || !fine.matches) { photo.style.removeProperty('--uni-depth-y'); photo.classList.remove('is-depth-tracking'); return; }
      const bounds = photo.getBoundingClientRect();
      const phase = Math.max(-1, Math.min(1, (window.innerHeight / 2 - bounds.top - bounds.height / 2) / (window.innerHeight / 2 + bounds.height / 2)));
      photo.style.setProperty('--uni-depth-y', `${(phase * Math.min(4, bounds.height * .005)).toFixed(2)}px`);
      photo.classList.add('is-depth-tracking');
    });
  };
  const scheduleDepth = () => { if (depthPhotos.size && depthFrame === null) depthFrame = window.requestAnimationFrame(measureDepth); };
  window.addEventListener('scroll', scheduleDepth, { passive: true });
  window.addEventListener('resize', scheduleDepth, { passive: true });
  fine.addEventListener('change', scheduleDepth);
  document.addEventListener('uni:motionchange', scheduleDepth);
  let manuallyPaused = false;
  try { window.localStorage.removeItem(preferenceKey); } catch (_) {}
  const allowed = element => !reduced.matches && !manuallyPaused && initialMotion !== 'none' && !element?.closest('[data-motion="none"]');
  const within = (scope, selector) => [...(scope instanceof Element && scope.matches(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  const own = (element, kind, setup) => {
    let kinds = instances.get(element);
    if (!kinds) { kinds = new Map(); instances.set(element, kinds); }
    if (!kinds.has(kind)) kinds.set(kind, setup());
  };
  function animate(element, frames, options) {
    if (!allowed(element) || typeof element.animate !== 'function') return null;
    const animation = element.animate(frames, { duration: 220, easing: 'cubic-bezier(.22,.68,0,1)', ...options });
    animations.add(animation);
    animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
    return animation;
  }
  function syncPreference() {
    document.body.dataset.motion = manuallyPaused ? 'none' : initialMotion;
    document.body.dataset.motionUser = manuallyPaused ? 'none' : 'auto';
    if (!allowed(document.body)) {
      animations.forEach(animation => { try { animation.finish(); } catch (_) { animation.cancel(); } });
      document.querySelectorAll('.will-reveal,.uni-motion-pending').forEach(item => item.classList.add('is-visible', 'is-motion-visible'));
    }
    document.dispatchEvent(new CustomEvent('uni:motionchange', { detail: { paused: manuallyPaused, reduced: reduced.matches, allowed: allowed(document.body) } }));
  }
  window.UniMotion = { allowed, paused: () => manuallyPaused, setPaused(value) {
    manuallyPaused = !!value;
    syncPreference();
  } };
  reduced.addEventListener('change', syncPreference);

  function openPanel(dialog) {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const old = panelStates.get(dialog);
    old?.animation?.cancel();
    const state = { closing: false, animation: null };
    panelStates.set(dialog, state);
    dialog.classList.add('uni-panel-managed');
    if (!dialog.open) dialog.showModal();
    state.animation = animate(dialog, [{ opacity: 0, transform: 'translateY(9px) scale(.99)' }, { opacity: 1, transform: 'none' }], { duration: 220 });
    const children = dialog.matches('.uni-mobile-menu') ? [...dialog.querySelectorAll('nav > a')] : [...dialog.children].filter(child => !child.matches('button,.uni-gallery-dialog__stage'));
    children.slice(0, 6).forEach((child, index) => animate(child, [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 160, delay: index * 22, fill: 'backwards' }));
  }
  function closePanel(dialog, options = {}) {
    if (!dialog?.open) return Promise.resolve();
    const previous = panelStates.get(dialog);
    if (previous?.closing && !options.immediate) return previous.promise;
    previous?.animation?.cancel();
    const state = { closing: true, animation: null, promise: null };
    panelStates.set(dialog, state);
    const complete = () => { if (panelStates.get(dialog) === state && dialog.open) dialog.close(); };
    if (options.immediate || !allowed(dialog)) { complete(); return Promise.resolve(); }
    state.animation = animate(dialog, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(7px) scale(.993)' }], { duration: 160 });
    state.promise = state.animation ? state.animation.finished.then(complete, () => undefined) : Promise.resolve().then(complete);
    return state.promise;
  }
  window.UniMotionPanels = { open: openPanel, close: closePanel };
  document.addEventListener('cancel', event => {
    if (event.target.matches('dialog.uni-mobile-menu,dialog.uni-compare-dialog,dialog.uni-gallery-dialog')) { event.preventDefault(); closePanel(event.target); }
  }, true);

  function initCard(card) {
    if (card.querySelector('form')) return;
    if (!card.matches('a[href]') && !card.querySelector('a[href],button')) return;
    return own(card, 'card', () => {
      const abort = new AbortController();
      const listen = { signal: abort.signal };
      const glow = document.createElement('span');
      glow.className = 'uni-card-glow'; glow.setAttribute('aria-hidden', 'true'); card.append(glow);
      card.classList.add('uni-card-reactive');
      let frame = null;
      let position = null;
      const reset = () => {
        if (frame !== null) window.cancelAnimationFrame(frame);
        frame = null; card.classList.remove('is-pointer-active');
        ['--uni-glow-x','--uni-glow-y','--uni-tilt-x','--uni-tilt-y'].forEach(key => card.style.removeProperty(key));
      };
      card.addEventListener('pointermove', event => {
        if (!fine.matches || event.pointerType !== 'mouse' || !allowed(card) || window.Shopify?.designMode) return;
        position = { x: event.clientX, y: event.clientY };
        if (frame !== null) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          const bounds = card.getBoundingClientRect();
          if (!bounds.width || !bounds.height) return;
          const x = Math.min(1, Math.max(0, (position.x - bounds.left) / bounds.width));
          const y = Math.min(1, Math.max(0, (position.y - bounds.top) / bounds.height));
          card.style.setProperty('--uni-glow-x', `${x * 100}%`); card.style.setProperty('--uni-glow-y', `${y * 100}%`);
          card.style.setProperty('--uni-tilt-x', `${(0.5 - y) * 1.1}deg`); card.style.setProperty('--uni-tilt-y', `${(x - 0.5) * 1.1}deg`);
          card.classList.add('is-pointer-active');
        });
      }, { ...listen, passive: true });
      card.addEventListener('pointerleave', reset, listen);
      card.addEventListener('pointercancel', reset, listen);
      document.addEventListener('uni:motionchange', reset, listen);
      fine.addEventListener('change', reset, listen);
      return () => { abort.abort(); reset(); glow.remove(); card.classList.remove('uni-card-reactive'); };
    });
  }
  function initPhoto(photo) {
    if (!photo.querySelector('img') || photo.closest('.uni-institution__card--authority,.uni-institution__card--recognition')) return;
    return own(photo, 'photo', () => {
      photo.classList.add('uni-depth-frame');
      let observer = null;
      let entered = false;
      const show = () => {
        if (entered) return;
        entered = true; photo.classList.add('is-depth-visible');
        if (!fine.matches && !photo.classList.contains('uni-reveal') && photo.getBoundingClientRect().top > 0) animate(photo, [{ opacity: .6, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 440 });
      };
      if (allowed(photo) && typeof IntersectionObserver === 'function' && !window.Shopify?.designMode) {
        observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) { show(); depthPhotos.add(photo); scheduleDepth(); }
          else { depthPhotos.delete(photo); photo.classList.remove('is-depth-tracking'); photo.style.removeProperty('--uni-depth-y'); }
        }), { threshold: .08 }); observer.observe(photo);
      } else photo.classList.add('is-depth-visible');
      return () => { observer?.disconnect(); depthPhotos.delete(photo); photo.classList.remove('is-depth-tracking'); photo.style.removeProperty('--uni-depth-y'); if (!depthPhotos.size && depthFrame !== null) { window.cancelAnimationFrame(depthFrame); depthFrame = null; } };
    });
  }
  const exclusiveSelector = '.uni-faq-item,.uni-program-faq .uni-accordion,.uni-partnership__service,.uni-learning-v6__item,.uni-project-v6__stage,.uni-model-v6__part,.uni-explore-v6__detail,.uni-program-term,.uni-admission__program,[data-faq-index]';
  const accordionGroups = new WeakMap();
  const accordionControllers = new WeakMap();
  let accordionGroupIndex = 0;
  function initAccordion(details) {
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    return own(details, 'accordion', () => {
      const abort = new AbortController();
      const content = [...details.children].filter(child => child !== summary);
      const originalName = details.getAttribute('name');
      const originalInert = content.map(child => child.inert);
      let animation = null;
      let contentAnimations = [];
      const reserved = details.hasAttribute('data-accordion-reserve');
      const gentle = Boolean(details.closest('.uni-distance,[data-contact-compact]'));
      let desired = details.open;
      let version = 0;
      let group = null;
      if (details.matches(exclusiveSelector) || details.closest('[data-accordion-set]')) {
        const parent = details.closest('[data-accordion-set]') || details.parentElement;
        if (!accordionGroups.has(parent)) accordionGroups.set(parent, { id: `uni-accordion-group-${++accordionGroupIndex}`, members: new Set() });
        group = accordionGroups.get(parent);
        details.dataset.accordionGroup = group.id;
        details.removeAttribute('name');
      }
      const sync = () => {
        summary.setAttribute('aria-expanded', String(desired));
        details.dataset.accordionState = desired ? 'open' : animation ? 'closing' : 'closed';
        details.toggleAttribute('data-accordion-closing', !desired && !!animation);
        content.forEach((child, index) => { child.inert = !desired || originalInert[index]; });
        details.dispatchEvent(new CustomEvent('uni:accordionstate', { bubbles: true }));
      };
      const settle = () => {
        const previous = animation;
        animation = null;
        details.open = desired;
        previous?.cancel();
        contentAnimations.forEach(item => item.cancel());
        contentAnimations = [];
        details.style.removeProperty('overflow');
        sync();
      };
      const set = (next, immediate = false) => {
        next = Boolean(next);
        if (next === desired && (animation || details.open === next)) return;
        const start = details.getBoundingClientRect().height;
        const token = ++version;
        desired = next;
        animation?.cancel();
        contentAnimations.forEach(item => item.cancel());
        contentAnimations = [];
        animation = null;
        if (next && group) group.members.forEach(other => { if (other !== controller && other.desired()) other.set(false, immediate); });
        if (immediate || !allowed(details) || typeof details.animate !== 'function') { settle(); return; }
        details.open = true;
        sync();
        const style = getComputedStyle(details);
        const vertical = ['borderTopWidth', 'borderBottomWidth', 'paddingTop', 'paddingBottom'].reduce((total, key) => total + (parseFloat(style[key]) || 0), 0);
        const end = next ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height + vertical;
        details.style.overflow = 'clip';
        if (reserved) {
          animation = animate(details, [{ opacity: 1 }, { opacity: 1 }], { duration: next ? 420 : 200 });
          contentAnimations = content.map(child => animate(child, [{ opacity: next ? 0 : 1 }, { opacity: next ? 1 : 0 }], { duration: next ? 200 : 180, delay: next ? 180 : 0, fill: 'both', easing: 'ease-out' })).filter(Boolean);
        } else {
          animation = animate(details, [{ height: `${start}px` }, { height: `${end}px` }], { duration: gentle ? 340 : next ? 280 : 250, easing: 'cubic-bezier(.25,.8,.25,1)' });
        }
        sync();
        if (animation) animation.finished.then(() => { if (token === version) settle(); }, () => undefined);
        else settle();
      };
      const controller = { set, desired: () => desired };
      accordionControllers.set(details, controller);
      group?.members.add(controller);
      summary.addEventListener('click', event => {
        if (event.target.closest('a,button,input,select,textarea') || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        event.preventDefault();
        set(!desired);
      }, { signal: abort.signal });
      details.addEventListener('keydown', event => {
        if (event.key === 'Escape' && desired) { event.preventDefault(); set(false); summary.focus({ preventScroll: true }); }
      }, { signal: abort.signal });
      details.addEventListener('toggle', () => {
        if (details.open === (desired || Boolean(animation))) return;
        if (details.open) set(true);
        else { version++; desired = false; settle(); }
      }, { signal: abort.signal });
      document.addEventListener('uni:motionchange', () => { if (!allowed(details)) { version++; settle(); } }, { signal: abort.signal });
      sync();
      return () => {
        version++; settle(); abort.abort(); group?.members.delete(controller); accordionControllers.delete(details);
        delete details.dataset.accordionGroup; delete details.dataset.accordionState;
        summary.removeAttribute('aria-expanded');
        content.forEach((child, index) => { child.inert = originalInert[index]; });
        if (originalName !== null) details.setAttribute('name', originalName);
      };
    });
  }
  window.UniAccordions = {
    set(details, open) { initAccordion(details); accordionControllers.get(details)?.set(open); },
    toggle(details) { initAccordion(details); const control = accordionControllers.get(details); control?.set(!control.desired()); },
    isOpen(details) { return accordionControllers.get(details)?.desired() ?? details.open; }
  };
  function initProcess(process) {
    const steps = [...process.querySelectorAll('[data-process-step]')];
    if (!steps.length) return;
    return own(process, 'process', () => {
      let progress = 0;
      let observer = null;
      const reveal = index => {
        progress = Math.max(progress, (index + 1) / steps.length);
        process.style.setProperty('--uni-process-progress', String(progress));
        steps[index].classList.add('is-process-visible');
      };
      const finish = () => { steps.forEach((_, index) => reveal(index)); observer?.disconnect(); };
      const changed = () => { if (!allowed(process)) finish(); };
      if (allowed(process) && typeof IntersectionObserver === 'function' && !window.Shopify?.designMode) {
        process.classList.add('uni-process-enhanced');
        observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { reveal(steps.indexOf(entry.target)); observer.unobserve(entry.target); } }), { threshold: .2 });
        steps.forEach(step => observer.observe(step));
      } else finish();
      document.addEventListener('uni:motionchange', changed);
      return () => { observer?.disconnect(); document.removeEventListener('uni:motionchange', changed); };
    });
  }
  function initConsent(panel) {
    return own(panel, 'consent', () => {
      let previousHidden = panel.hidden;
      let ghost = null;
      const disposeGhost = () => { ghost?.remove(); ghost = null; };
      const enter = () => animate(panel, [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 230 });
      const observer = new MutationObserver(() => {
        if (panel.hidden === previousHidden) return;
        previousHidden = panel.hidden; disposeGhost();
        if (!panel.hidden) { enter(); return; }
        if (!allowed(panel)) return;
        ghost = panel.cloneNode(true);
        ghost.removeAttribute('data-consent'); ghost.removeAttribute('id'); ghost.removeAttribute('hidden'); ghost.setAttribute('aria-hidden', 'true'); ghost.setAttribute('inert', '');
        ghost.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        ghost.classList.add('uni-panel-ghost');
        panel.after(ghost);
        const leaving = animate(ghost, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(8px)' }], { duration: 150 });
        if (leaving) leaving.finished.then(disposeGhost, disposeGhost); else disposeGhost();
      });
      observer.observe(panel, { attributes: true, attributeFilter: ['hidden'] });
      if (!panel.hidden) enter();
      return () => { observer.disconnect(); disposeGhost(); };
    });
  }
  function initGallery(gallery) {
    const dialog = gallery.querySelector('dialog');
    const links = [...gallery.querySelectorAll('[data-uni-gallery-open]')];
    if (!dialog || !links.length || typeof dialog.showModal !== 'function') return;
    return own(gallery, 'gallery', () => {
      const abort = new AbortController();
      const listen = { signal: abort.signal };
      const image = dialog.querySelector('[data-uni-gallery-image]');
      const stage = dialog.querySelector('.uni-gallery-dialog__stage');
      const caption = dialog.querySelector('[data-uni-gallery-caption]');
      const status = dialog.querySelector('[data-gallery-load-status]');
      const thumbnailStrip = dialog.querySelector('[data-gallery-thumbnails]');
      const thumbnails = [...dialog.querySelectorAll('[data-gallery-thumbnail]')];
      if (thumbnailStrip) thumbnailStrip.hidden = thumbnails.length < 2;
      const syncThumbnails = current => {
        thumbnails.forEach((button, i) => {
          button.setAttribute('aria-pressed', String(i === current));
          if (i === current && thumbnailStrip && dialog.open) {
            const buttonBounds = button.getBoundingClientRect(), stripBounds = thumbnailStrip.getBoundingClientRect();
            if (buttonBounds.left < stripBounds.left || buttonBounds.right > stripBounds.right) {
              thumbnailStrip.scrollTo({ left: button.offsetLeft - thumbnailStrip.offsetLeft - (thumbnailStrip.clientWidth - button.offsetWidth) / 2, behavior: allowed(image) ? 'smooth' : 'instant' });
            }
          }
        });
      };
      const cache = new Map();
      const pointers = new Set();
      let index = 0, requested = 0, version = 0, opener = null, gesture = null, imageAnimation = null, oldImage = null;
      const cleanImage = () => { imageAnimation?.cancel(); imageAnimation = null; oldImage?.remove(); oldImage = null; image.style.removeProperty('transform'); };
      const loaded = link => {
        const source = link.dataset.fullSrc;
        if (!cache.has(source)) {
          const preload = new Image(); preload.decoding = 'async'; preload.src = source;
          const promise = typeof preload.decode === 'function' ? preload.decode() : new Promise((resolve, reject) => { preload.onload = resolve; preload.onerror = reject; });
          cache.set(source, promise.then(() => source).catch(error => { cache.delete(source); throw error; }));
        }
        return cache.get(source);
      };
      const expandFrom = origin => {
        if (!origin || !allowed(image)) return;
        const from = origin.getBoundingClientRect(); const to = image.getBoundingClientRect();
        if (!from.width || !to.width || from.bottom < 0 || from.top > window.innerHeight) return;
        const x = from.left + from.width / 2 - to.left - to.width / 2;
        const y = from.top + from.height / 2 - to.top - to.height / 2;
        const scale = Math.min(1, Math.max(.3, from.width / to.width));
        imageAnimation = animate(image, [{ opacity: .6, transform: `translate(${x}px,${y}px) scale(${scale})` }, { opacity: 1, transform: 'none' }], { duration: 300 });
      };
      const show = async (next, direction = 1, fromLink = null) => {
        requested = (next + links.length) % links.length;
        const target = requested; const link = links[target]; const ticket = ++version;
        const opening = !dialog.open;
        cleanImage();
        if (opening) {
          opener = fromLink || link;
          const thumbnail = link.querySelector('img');
          image.src = thumbnail?.currentSrc || thumbnail?.src || link.dataset.fullSrc;
          image.alt = link.dataset.imageAlt || '';
          caption.textContent = link.dataset.caption || image.alt;
          openPanel(dialog); expandFrom(thumbnail);
        }
        dialog.setAttribute('aria-busy', 'true');
        if (status) { status.textContent = dialog.dataset.galleryLoadingLabel || ''; status.hidden = !status.textContent; }
        dialog.querySelectorAll('[data-uni-gallery-prev],[data-uni-gallery-next]').forEach(button => { button.hidden = links.length < 2; });
        try {
          const source = await loaded(link);
          if (ticket !== version || !dialog.open || !gallery.isConnected || panelStates.get(dialog)?.closing) return;
          if (!opening && target !== index && allowed(image)) {
            oldImage = image.cloneNode(true); oldImage.removeAttribute('data-uni-gallery-image'); oldImage.removeAttribute('id'); oldImage.alt = ''; oldImage.setAttribute('aria-hidden', 'true'); oldImage.classList.add('uni-gallery-dialog__previous'); stage.append(oldImage);
            const ghost = oldImage;
            const outgoing = animate(ghost, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translateX(${-direction * 18}px)` }], { duration: 210 });
            outgoing?.finished.then(() => ghost.remove(), () => ghost.remove());
          }
          image.src = source; image.alt = link.dataset.imageAlt || ''; caption.textContent = link.dataset.caption || image.alt;
          if (!opening && target !== index) imageAnimation = animate(image, [{ opacity: .15, transform: `translateX(${direction * 18}px)` }, { opacity: 1, transform: 'none' }], { duration: 230 });
          index = target; gallery.dataset.galleryIndex = String(index); syncThumbnails(index);
          dialog.removeAttribute('aria-busy');
          if (status) { status.hidden = true; status.textContent = ''; }
        } catch (_) {
          if (ticket !== version || !dialog.open) return;
          requested = index; syncThumbnails(index); dialog.removeAttribute('aria-busy');
          if (status) { status.textContent = dialog.dataset.galleryErrorLabel || ''; status.hidden = !status.textContent; }
        }
      };
      const close = (immediate = false) => {
        version++; gesture = null; pointers.clear(); cleanImage();
        const thumbnail = opener?.querySelector('img');
        if (!immediate && thumbnail && allowed(image)) {
          const to = thumbnail.getBoundingClientRect(), from = image.getBoundingClientRect();
          if (to.width && from.width && to.bottom > 0 && to.top < window.innerHeight) {
            const x = to.left + to.width / 2 - from.left - from.width / 2;
            const y = to.top + to.height / 2 - from.top - from.height / 2;
            const scale = Math.min(1, Math.max(.3, to.width / from.width));
            imageAnimation = animate(image, [{ opacity: 1, transform: 'none' }, { opacity: .3, transform: `translate(${x}px,${y}px) scale(${scale})` }], { duration: 160 });
          }
        }
        return closePanel(dialog, { immediate });
      };
      gallery.addEventListener('click', event => {
        const link = event.target.closest('[data-uni-gallery-open]');
        if (link && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) { event.preventDefault(); show(links.indexOf(link), 1, link); }
        else if (event.target.closest('[data-uni-gallery-close]')) close();
        else if (event.target.closest('[data-uni-gallery-prev]')) show(requested - 1, -1);
        else if (event.target.closest('[data-uni-gallery-next]')) show(requested + 1, 1);
        else if (event.target.closest('[data-gallery-thumbnail]')) {
          const target = Number(event.target.closest('[data-gallery-thumbnail]').dataset.galleryThumbnail);
          show(target, target >= requested ? 1 : -1);
        }
        else if (event.target === dialog) {
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
        }
      }, listen);
      dialog.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input,textarea,select')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); show(requested + (event.key === 'ArrowLeft' ? -1 : 1), event.key === 'ArrowLeft' ? -1 : 1); }
      }, listen);
      dialog.addEventListener('cancel', event => { event.preventDefault(); close(); }, listen);
      dialog.addEventListener('close', () => {
        version++; cleanImage(); dialog.removeAttribute('aria-busy');
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      }, listen);
      const zoomChanged = () => { image.style.touchAction = (window.visualViewport?.scale || 1) > 1.02 ? 'auto' : 'pan-y pinch-zoom'; };
      window.visualViewport?.addEventListener('resize', zoomChanged, listen); zoomChanged();
      image.addEventListener('dragstart', event => event.preventDefault(), listen);
      image.addEventListener('pointerdown', event => {
        pointers.add(event.pointerId);
        if (pointers.size !== 1 || event.isPrimary === false || event.button !== 0 || (window.visualViewport?.scale || 1) > 1.02) { gesture = null; cleanImage(); return; }
        cleanImage(); gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, horizontal: false };
      }, listen);
      image.addEventListener('pointermove', event => {
        if (!gesture || pointers.size !== 1 || gesture.id !== event.pointerId) return;
        const x = event.clientX - gesture.x, y = event.clientY - gesture.y;
        if (!gesture.horizontal && Math.abs(y) > 12 && Math.abs(y) > Math.abs(x)) { gesture = null; image.style.removeProperty('transform'); return; }
        if (Math.abs(x) > 12 && Math.abs(x) > Math.abs(y) * 1.3) {
          gesture.horizontal = true;
          if (event.cancelable) event.preventDefault(); image.setPointerCapture?.(event.pointerId);
          if (allowed(image)) image.style.transform = `translateX(${Math.max(-36, Math.min(36, x * .22))}px)`;
        }
      }, { ...listen, passive: false });
      const end = event => {
        pointers.delete(event.pointerId);
        if (!gesture || gesture.id !== event.pointerId) return;
        const x = event.clientX - gesture.x;
        const change = event.type !== 'pointercancel' && gesture.horizontal && Math.abs(x) > 44;
        gesture = null; image.style.removeProperty('transform');
        if (image.hasPointerCapture?.(event.pointerId)) image.releasePointerCapture?.(event.pointerId);
        if (change) show(requested + (x < 0 ? 1 : -1), x < 0 ? 1 : -1);
      };
      image.addEventListener('pointerup', end, listen); image.addEventListener('pointercancel', end, listen);
      return () => { close(true); abort.abort(); };
    });
  }
  function init(scope = document) {
    within(scope, '.uni-program-card,.uni-news-card,.uni-bento__card,.uni-event-card,.uni-editorial-bridge__card').forEach(initCard);
    within(scope, '.uni-story__media,.uni-method__photo,.uni-program-hero__media,.uni-institution__image,.uni-gallery-item > a').forEach(initPhoto);
    within(scope, `${exclusiveSelector},.uni-consent__preferences,[data-motion-accordion]`).forEach(initAccordion);
    within(scope, '[data-process-motion]').forEach(initProcess);
    within(scope, '[data-uni-gallery]').forEach(initGallery);
    if (typeof MutationObserver === 'function') within(scope, '[data-consent]').forEach(initConsent);
    syncPreference();
  }
  function unload(scope) {
    instances.forEach((kinds, element) => { if (scope.contains(element) || !element.isConnected) { kinds.forEach(dispose => dispose?.()); instances.delete(element); } });
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
